import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Pill, ArrowRight, PieChart, Clock, CheckCircle, TrendingUp, Heart, Timer, AlertCircle } from "lucide-react";

export const MedicationHomeSection = () => {
  return (
    <div className="grid md:grid-cols-2 gap-12 items-center px-8 max-h-[470px] relative overflow-hidden">
      {/* Transparent background */}
      <div className="absolute inset-0 bg-transparent"></div>
      
      <div className="space-y-6 relative z-10">
        <h2 className="text-3xl font-bold">Gestion des médicaments</h2>
        <p className="text-lg text-muted-foreground">
          Suivez vos traitements et ne manquez jamais une prise avec notre système intelligent de rappels
        </p>
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Rappels automatiques personnalisés</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Suivi de l'observance en temps réel</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-1.5 rounded-full bg-primary" />
            <span>Statistiques détaillées de vos prises</span>
          </div>
        </div>
        <Button asChild>
          <Link to="/medicaments">Gérer mes médicaments</Link>
        </Button>
      </div>
      
      <div className="relative z-10 flex justify-center items-center">
        <div className="w-full h-[320px] bg-gradient-to-br from-green-50 via-blue-50 to-teal-50 rounded-xl shadow-lg p-6 relative overflow-hidden">
          
          {/* Central medication visualization */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <div className="relative">
              <div className="absolute inset-0 bg-green-300/20 rounded-full h-32 w-32 animate-ping" style={{animationDuration: "3s"}}></div>
              <div className="absolute inset-0 bg-green-300/30 rounded-full h-32 w-32 animate-pulse" style={{animationDuration: "2s"}}></div>
              <div className="relative bg-gradient-to-br from-green-400 to-blue-500 h-32 w-32 rounded-full flex items-center justify-center shadow-lg">
                <Pill className="h-16 w-16 text-white" />
              </div>
            </div>
          </div>
          
          {/* Rotating medication circle */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-64 w-64 rounded-full border-4 border-dashed border-green-200 animate-spin" style={{animationDuration: "20s"}}></div>
          </div>
          
          {/* Pills and medical icons arranged in a circle */}
          {[0, 1, 2, 3, 4, 5].map((i) => {
            const angle = (i * 60) * (Math.PI / 180);
            const x = 80 * Math.cos(angle);
            const y = 80 * Math.sin(angle);
            
            return (
              <div 
                key={i}
                className="absolute w-12 h-12 rounded-full bg-white shadow-md flex items-center justify-center transition-transform hover:scale-110"
                style={{
                  transform: `translate(${x}px, ${y}px)`,
                  animation: `pulse ${2 + i * 0.2}s ease-in-out infinite`,
                  animationDelay: `${i * 0.2}s`,
                  top: '50%',
                  left: '50%',
                  marginTop: '-24px',
                  marginLeft: '-24px'
                }}
              >
                {i === 0 && <Pill className="h-6 w-6 text-blue-500" />}
                {i === 1 && <CheckCircle className="h-6 w-6 text-green-500" />}
                {i === 2 && <Timer className="h-6 w-6 text-orange-500" />}
                {i === 3 && <AlertCircle className="h-6 w-6 text-red-500" />}
                {i === 4 && <Clock className="h-6 w-6 text-purple-500" />}
                {i === 5 && <Heart className="h-6 w-6 text-pink-500" />}
              </div>
            );
          })}
          
          {/* Floating info cards */}
          <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.2s"}}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-sm text-green-900">Rappels intelligents</h4>
            </div>
            <p className="text-xs text-gray-600">
              Notifications personnalisées selon vos habitudes et besoins
            </p>
          </div>
          
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.3s"}}>
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-sm text-green-900">Suivi précis</h4>
            </div>
            <p className="text-xs text-gray-600">
              Confirmation de prise et historique détaillé de vos traitements
            </p>
          </div>
          
          <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.4s"}}>
            <div className="flex items-center gap-2 mb-2">
              <PieChart className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-sm text-green-900">Statistiques</h4>
            </div>
            <p className="text-xs text-gray-600">
              Analysez vos habitudes et optimisez votre observance
            </p>
          </div>
          
          <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-lg shadow-sm max-w-[180px] animate-fade-in" style={{animationDelay: "0.5s"}}>
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              <h4 className="font-semibold text-sm text-green-900">Observance</h4>
            </div>
            <p className="text-xs text-gray-600">
              Suivez votre progression et améliorez votre santé
            </p>
          </div>
          
          {/* Floating particles */}
          {Array.from({length: 20}).map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-green-300/30"
              style={{
                width: `${3 + Math.random() * 5}px`,
                height: `${3 + Math.random() * 5}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${3 + Math.random() * 4}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 2}s`
              }}
            />
          ))}
          
          {/* Animation styles */}
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 0.8;
                  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) scale(1);
                }
                50% {
                  opacity: 1;
                  transform: translate(var(--tw-translate-x), var(--tw-translate-y)) scale(1.05);
                }
              }
              
              @keyframes float {
                0%, 100% {
                  transform: translateY(0);
                }
                50% {
                  transform: translateY(-10px);
                }
              }
              
              @keyframes fade-in {
                from {
                  opacity: 0;
                  transform: translateY(10px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }
              
              .animate-fade-in {
                animation: fade-in 0.5s ease-out forwards;
              }
            `}
          </style>
        </div>
      </div>
    </div>
  );
};
